import { Shield, Terminal, Bug, Lock, AlertTriangle, Radio, Fingerprint, Globe } from 'lucide-react'
import { useArticles } from '../hooks/useArticles'
import type { Article } from '../types/blog'

/* ─────────────────────────────────────────
   Helpers
   ───────────────────────────────────────── */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function categoryIcon(category: string) {
  switch (category) {
    case 'threat-intel':   return <AlertTriangle size={18} />
    case 'vulnerability':  return <Bug size={18} />
    case 'malware':        return <Shield size={18} />
    case 'privacy':        return <Lock size={18} />
    default:               return <Terminal size={18} />
  }
}

/* ─────────────────────────────────────────
   Badge
   ───────────────────────────────────────── */
const BADGE: Record<string, string> = {
  critical: 'badge-critical text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
  high:     'badge-high     text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
  medium:   'badge-medium   text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
  low:      'badge-low      font-mono text-[10px] uppercase tracking-widest px-2.5 py-1',
}

/* ─────────────────────────────────────────
   Page
   ───────────────────────────────────────── */
export default function Home() {
  const { articles, loading, error } = useArticles({ limit: 6 })

  return (
    <div className="bg-terminal-bg min-h-screen">

      {/* ════════════════════════════════
          HERO — AI-generated background
          ════════════════════════════════ */}
      <section
        className="relative overflow-hidden flex flex-col"
        style={{
          minHeight: '92vh',
          backgroundImage: 'url(/assets/hero-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center -52px',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Thin fade at top — blends section into header, doesn't clip sword */}
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{ height: '28px', background: 'linear-gradient(to bottom, rgba(10,10,10,0.95) 0%, transparent 100%)' }}
        />

        {/* Edge vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 80% at 50% 50%, transparent 30%, rgba(0,0,0,0.55) 100%)',
          }}
        />

        {/* #C5813C amber glow — lower-center, where logo bottom/shield meets the floor */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: '18%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '520px',
            height: '220px',
            background: 'radial-gradient(ellipse at center, rgba(197,129,60,0.22) 0%, rgba(197,129,60,0.08) 50%, transparent 75%)',
          }}
        />

        {/* #C5813C amber floor sweep — bottom edge glow */}
        <div
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{
            height: '200px',
            background: 'linear-gradient(to top, rgba(10,10,10,0.97) 0%, rgba(197,129,60,0.08) 55%, transparent 100%)',
          }}
        />

        {/* Left tower base amber warmth */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: 0, left: 0,
            width: '30%', height: '50%',
            background: 'radial-gradient(ellipse at 0% 100%, rgba(197,129,60,0.12) 0%, transparent 65%)',
          }}
        />

        {/* Right tower base amber warmth */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: 0, right: 0,
            width: '30%', height: '50%',
            background: 'radial-gradient(ellipse at 100% 100%, rgba(197,129,60,0.12) 0%, transparent 65%)',
          }}
        />

        {/* ── Tagline + CTAs — pinned to bottom ── */}
        <div className="absolute bottom-0 left-0 right-0 z-10 flex flex-col items-center pb-10 px-4 gap-5">
          <p
            className="font-mono uppercase text-white tracking-[0.22em] text-center"
            style={{ fontSize: 'clamp(11px, 1.05vw, 14px)', textShadow: '0 2px 12px rgba(0,0,0,1), 0 0 30px rgba(0,0,0,0.9)' }}
          >
            Trust But Verify !!!
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <HeroButton primary>Initiate Mission</HeroButton>
            <HeroButton>Access The Archive</HeroButton>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════
          STAT STRIP — below hero
          ════════════════════════════════ */}
      <div
        className="border-y"
        style={{ borderColor: 'rgba(85,107,47,0.35)', background: 'rgba(10,15,8,0.9)' }}
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4">
            <TacticalWidget icon={<Shield size={15} />} label="Threats Analyzed" value="1,247" variant="critical" />
            <TacticalWidget icon={<Bug size={15} />}    label="Vulnerabilities"  value="89"    variant="critical" />
            <TacticalWidget icon={<Terminal size={15} />} label="Active Missions" value="342"  variant="amber"    />
            <TacticalWidget icon={<Lock size={15} />}   label="Systems Secured"  value="1.2K"  variant="olive"    />
          </div>
        </div>
      </div>

      {/* ════════════════════════════════
          OPS PANEL — Status strip
          ════════════════════════════════ */}
      <div
        className="border-b"
        style={{ borderColor: 'rgba(85,107,47,0.3)', background: 'rgba(12,18,9,0.85)' }}
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center gap-6 py-2 overflow-x-auto">
            <StatusPill icon={<Radio size={12} />}   label="SIGNALS ACTIVE"    color="text-status-secure" />
            <StatusPill icon={<Globe size={12} />}   label="NETWORK MONITORED" color="text-bear" />
            <StatusPill icon={<Fingerprint size={12} />} label="ID VERIFIED"   color="text-military" />
            <div className="ml-auto flex items-center gap-2 text-[10px] font-mono text-text-gray whitespace-nowrap">
              <span className="status-dot status-online inline-block" />
              SECURE CHANNEL ESTABLISHED
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════
          INTEL FEED — Blog cards
          ════════════════════════════════ */}
      <section className="container mx-auto px-4 py-12 max-w-7xl">

        {/* Section header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="h-px flex-1" style={{ background: 'rgba(85,107,47,0.4)' }} />
          <div className="flex items-center gap-2">
            <Terminal style={{ color: '#7ab648' }} size={16} />
            <span className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: '#7ab648' }}>
              Latest Intelligence Reports
            </span>
          </div>
          <div className="h-px flex-1" style={{ background: 'rgba(85,107,47,0.4)' }} />
        </div>

        {loading && (
          <div className="terminal-window p-6 text-center">
            <p className="font-mono text-status-secure text-sm animate-pulse">
              $ SCANNING INTEL FEEDS...<span className="animate-blink">_</span>
            </p>
          </div>
        )}

        {error && (
          <div className="terminal-window p-6 text-center">
            <p className="font-mono text-kgb-red text-sm">[ERROR] {error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article) => (
              <IntelCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </section>

      {/* ════════════════════════════════
          TERMINAL BLOCK — Design system element
          ════════════════════════════════ */}
      <section className="container mx-auto px-4 pb-12 max-w-7xl">
        <div className="grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 terminal-window">
            <div className="terminal-header">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-kgb-red opacity-80" />
                <div className="w-2.5 h-2.5 rounded-full bg-status-warning opacity-80" />
                <div className="w-2.5 h-2.5 rounded-full bg-status-online opacity-80" />
              </div>
              <span className="ml-3 terminal-header-title">
                CLASSIFIED_OPERATIONS.sh
              </span>
            </div>
            <div className="terminal-body text-sm space-y-1 p-5">
              <p><span className="text-text-gray">$</span> <span className="text-white">import</span> hackerscode</p>
              <p><span className="text-text-gray">$</span> <span className="text-white">sudo</span> <span className="text-kgb-red">-a</span> root</p>
              <p className="mt-3 text-[#00FF41]">function <span className="text-white">scan</span>() {'{'}</p>
              <p className="pl-4 text-[#00FF41]">const <span className="text-white">target</span> = .fringe.encrypted</p>
              <p className="pl-4 text-[#00FF41]">const <span className="text-white">key</span> = decrypt(target.sh)</p>
              <p className="text-[#00FF41]">{'}'}</p>
              <p className="mt-3"><span className="text-white">if</span> ( see ) {'{'}</p>
              <p className="pl-4 text-[#00FF41]">const smoSSIS_description.secure_ent$()</p>
              <p>{'}'}</p>
              <p className="mt-3 text-text-gray"># output to secure channel</p>
              <p>CLASSIFIED_DECRYPTION.sh</p>
              <p>Encrypt source: active</p>
              <p className="flex items-center gap-1">$ <span className="w-2 h-4 bg-status-secure animate-blink inline-block" /></p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <ThreatReadout level="CRITICAL" count="3" variant="critical" />
            <ThreatReadout level="ACTIVE MISSIONS" count="7" variant="amber" />
            <ThreatReadout level="CRITICAL" count="12" variant="critical" />
            <ThreatReadout level="ACTIVE MISSIONS" count="5" variant="olive" />
            <button className="btn-mission w-full flex items-center justify-center gap-2 mt-1">
              <span>★</span>
              Initialize Encrypted Upload
            </button>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════
          CTA BANNER
          ════════════════════════════════ */}
      <section className="container mx-auto px-4 pb-16 max-w-7xl">
        <div
          className="p-8 md:p-10 border border-kgb-red/40 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(220,20,60,0.12) 0%, rgba(180,0,0,0.06) 50%, transparent 100%)',
          }}
        >
          {/* Corner accent */}
          <div className="absolute top-0 left-0 w-20 h-0.5 bg-kgb-red" />
          <div className="absolute top-0 left-0 w-0.5 h-20 bg-kgb-red" />
          <div className="absolute bottom-0 right-0 w-20 h-0.5 bg-kgb-red" />
          <div className="absolute bottom-0 right-0 w-0.5 h-20 bg-kgb-red" />

          <div className="max-w-3xl">
            <p className="font-mono text-[10px] text-kgb-red uppercase tracking-[0.4em] mb-3">
              ★ &nbsp; Secure Channel Access
            </p>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-3 uppercase tracking-wide">
              Join the Security Operations Network
            </h2>
            <p className="text-text-gray font-mono text-sm mb-6 leading-relaxed">
              Real-time threat intelligence, vulnerability alerts, and classified security research —
              delivered to your secure terminal nightly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="enter.secure@email.address"
                className="flex-1 bg-black text-white px-4 py-3 border border-border-gray focus:border-kgb-red focus:outline-none font-mono text-sm placeholder:text-text-gray/50"
              />
              <button className="btn-mission flex items-center gap-2 whitespace-nowrap">
                <Lock size={14} />
                Gain Access
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}

/* ─────────────────────────────────────────
   Sub-components
   ───────────────────────────────────────── */

function TacticalWidget({
  icon, label, value, variant = 'default',
}: {
  icon: React.ReactNode
  label: string
  value: string
  variant?: 'critical' | 'amber' | 'olive' | 'default'
}) {
  const accentColor = {
    critical: '#DC143C',
    amber:    '#CD853F',
    olive:    '#556B2F',
    default:  '#2a2a2a',
  }[variant]

  const valueColor = {
    critical: 'text-kgb-red',
    amber:    'text-bear',
    olive:    'text-military',
    default:  'text-white',
  }[variant]

  return (
    <div
      className="ops-widget px-4 py-3 cursor-default border-y border-border-gray/30"
      style={{ borderLeftColor: accentColor }}
    >
      <div className="flex items-center gap-1.5 mb-1" style={{ color: accentColor }}>
        {icon}
        <span className="font-mono text-[9px] uppercase tracking-widest text-text-gray">
          {label}
        </span>
      </div>
      <div className={`text-2xl font-display font-bold ${valueColor}`}>{value}</div>
    </div>
  )
}

function IntelCard({ article }: { article: Article }) {
  return (
    <a
      href={article.source_url}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
    >
      <article className="bracket-card bg-panel-bg border border-border-gray hover:border-kgb-red/50 transition-all group cursor-pointer h-full">
        <div className="p-5 flex flex-col h-full">
          {/* Top row: icon + badge */}
          <div className="flex items-start justify-between mb-4">
            <div
              className="p-2.5 border transition-colors"
              style={{
                background: '#0f0f0f',
                borderColor: '#2a2a2a',
              }}
            >
              <span className="text-kgb-red">{categoryIcon(article.category)}</span>
            </div>
            <span className={BADGE[article.threat_level]}>
              {article.threat_level}
            </span>
          </div>

          {/* Category */}
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-military mb-2">
            {article.category}
          </p>

          {/* Title */}
          <h3 className="font-display font-bold text-white text-base leading-snug mb-3 group-hover:text-kgb-red transition-colors">
            {article.title}
          </h3>

          {/* Excerpt */}
          {article.excerpt && (
            <p className="text-xs text-text-gray font-mono leading-relaxed mb-4 line-clamp-3 flex-1">
              {article.excerpt}
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border-gray/50 mt-auto">
            <div className="flex flex-col gap-0.5">
              <span className="font-mono text-[10px] text-text-gray/60">
                {formatDate(article.published_at)}
              </span>
              <span className="font-mono text-[10px] text-bear/70 uppercase tracking-wide">
                SOURCE: {article.source_name}
              </span>
            </div>
            <span className="font-mono text-[10px] text-kgb-red/70 group-hover:text-kgb-red tracking-widest uppercase transition-colors">
              Read Report →
            </span>
          </div>
        </div>
      </article>
    </a>
  )
}

function StatusPill({
  icon, label, color,
}: { icon: React.ReactNode; label: string; color: string }) {
  return (
    <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider whitespace-nowrap ${color}`}>
      {icon}
      {label}
    </div>
  )
}

function HeroButton({
  children, primary = false,
}: { children: React.ReactNode; primary?: boolean }) {
  return (
    <button
      className="px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-all duration-150 cursor-pointer"
      style={{
        background: primary ? 'rgba(220,20,60,0.85)' : 'rgba(20,20,20,0.80)',
        border: primary ? '1px solid rgba(220,20,60,0.9)' : '1px solid rgba(255,255,255,0.22)',
        backdropFilter: 'blur(4px)',
      }}
      onMouseEnter={e => {
        if (primary) {
          e.currentTarget.style.background = 'rgba(220,20,60,1)'
        } else {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)'
        }
      }}
      onMouseLeave={e => {
        if (primary) {
          e.currentTarget.style.background = 'rgba(220,20,60,0.85)'
        } else {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'
        }
      }}
    >
      {children}
    </button>
  )
}

function ThreatReadout({
  level, count, variant = 'critical',
}: { level: string; count: string; variant?: 'critical' | 'amber' | 'olive' }) {
  const palette = {
    critical: {
      bg:     'linear-gradient(135deg, rgba(80,5,5,0.9) 0%, rgba(25,0,0,0.97) 100%)',
      border: 'rgba(220,20,60,0.55)',
      glow:   '0 0 18px rgba(220,20,60,0.2), inset 0 0 30px rgba(120,0,0,0.15)',
      label:  'rgba(180,80,80,0.8)',
      value:  '#DC143C',
      vshadow:'0 0 16px rgba(220,20,60,0.75)',
    },
    amber: {
      bg:     'linear-gradient(135deg, rgba(60,30,5,0.9) 0%, rgba(20,10,0,0.97) 100%)',
      border: 'rgba(205,133,63,0.5)',
      glow:   '0 0 18px rgba(205,133,63,0.15), inset 0 0 30px rgba(100,60,0,0.12)',
      label:  'rgba(180,140,80,0.8)',
      value:  '#CD853F',
      vshadow:'0 0 14px rgba(205,133,63,0.6)',
    },
    olive: {
      bg:     'linear-gradient(135deg, rgba(20,35,8,0.9) 0%, rgba(8,14,3,0.97) 100%)',
      border: 'rgba(85,107,47,0.5)',
      glow:   '0 0 18px rgba(85,107,47,0.15), inset 0 0 30px rgba(40,60,10,0.12)',
      label:  'rgba(130,160,80,0.8)',
      value:  '#7ab648',
      vshadow:'0 0 14px rgba(122,182,72,0.5)',
    },
  }[variant]

  const isMission = level.toLowerCase().includes('mission') || level.toLowerCase().includes('active')

  return (
    <div
      className="relative overflow-hidden p-4"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow: palette.glow,
      }}
    >
      {/* Corner accent lines — top-left */}
      <div className="absolute top-0 left-0 w-5 h-px" style={{ background: palette.value }} />
      <div className="absolute top-0 left-0 w-px h-5" style={{ background: palette.value }} />
      {/* Corner accent lines — bottom-right */}
      <div className="absolute bottom-0 right-0 w-5 h-px" style={{ background: palette.value }} />
      <div className="absolute bottom-0 right-0 w-px h-5" style={{ background: palette.value }} />

      <p
        className="font-mono text-[9px] uppercase tracking-[0.25em] mb-1"
        style={{ color: palette.label }}
      >
        {isMission ? 'Active Missions:' : 'Threat Level:'}
      </p>
      <p
        className="font-display font-bold text-2xl uppercase"
        style={{ color: palette.value, textShadow: palette.vshadow, letterSpacing: '0.05em' }}
      >
        {isMission ? count : level}
      </p>
      {!isMission && (
        <p
          className="font-display font-bold text-4xl mt-1"
          style={{ color: palette.value, textShadow: palette.vshadow }}
        >
          {count}
        </p>
      )}
    </div>
  )
}
